/**
 * What the taskpane needs to know about reports: the template contract, the runner type guards,
 * and the operations the UI drives. `helpers/common.ts` behind this is report-internal.
 */
import type { FeatureFlags } from "../../../config/featureFlags";
import { cacheableData } from "../../workbook/cache";
import { getSheetsByReportType, ReportSheetInfo } from "../../workbook/customProperties";
import { groupDataByKey } from "../helpers/groupings";
import { ReportGroup } from "./groups";
import type { ReportStaleCheck } from "../../datasources/staleness";

export interface ReportOptionDefinitionBase {
  key: string;
  label: string;
  /** If set, option is only shown when this feature flag is truthy */
  featureFlag?: keyof FeatureFlags;
  /** If set, control is disabled when this returns true */
  disabledWhen?: (options: cacheableData) => boolean;
  /** If set, control is hidden on sheets with none of these named ranges, since the option has nothing to act on */
  requiredNamedRanges?: string[];
}

export interface ReportOptionDefinitionCheckbox extends ReportOptionDefinitionBase {
  controlType: "checkbox";
}

export interface ReportOptionDefinitionDropdown extends ReportOptionDefinitionBase {
  controlType: "dropdown";
  multiple?: boolean;
  /** Fetcher for dropdown choices. */
  getDropdownOptions: () => Promise<Record<string, unknown>[]>;
  emptyOptionLabel: string;
  /** Property name on each choice object for the stored value (e.g. "id") */
  valueKey: string;
  /** Property name on each choice object for the display label (e.g. "name") */
  labelKey: string;
}

export type ReportOptionDefinition = ReportOptionDefinitionCheckbox | ReportOptionDefinitionDropdown;

type SingleSheetReportRunner = (worksheet: Excel.Worksheet) => Promise<void>;

export interface ReportTemplate {
  worksheetName: string;
  reportType: ReportDefinition;
  reportDataSource: ReportDataSource;
  dataNamedRange: string;
  options: cacheableData;
  reportRunner?: SingleSheetReportRunner | MultiSheetReportRunner;
  splitByKey?: string;
}

export interface ReportDefinition {
  name: string;
  /** Which section of the Reports page this lands in. Declared, not derived — `gcStaff` is why. */
  group: ReportGroup;
  identifyingNamedRange: string;
  reportDataSource: ReportDataSource;
  options?: cacheableData;
  optionDefinitions?: ReportOptionDefinition[];
  reportRunner?: SingleSheetReportRunner | MultiSheetReportRunner;
  splitByKey?: string;
  staleChecks?: ReportStaleCheck[];
}

/**
 * Answers "does the loaded datasource have rows for this report?". Resolves its own datasource's
 * .metadata marker and returns [] when that marker is absent; throws only on a real failure. The
 * distinction drives the card's empty state — a throw is read as "could not tell" and preserves the
 * previous status, so signalling "nothing loaded" that way leaves a stale card.
 */
export type ReportDataSource = () => Promise<unknown[]>;
export type MultiSheetReportRunner = () => Promise<void>;

const readableLabelOptions: Record<string, string> = {
  floor_group: "Buildings",
  estimate_use_group: "Use Groups",
};

export const hasMultiSheetRunner = (
  template: ReportTemplate
): template is ReportTemplate & { reportRunner: MultiSheetReportRunner; splitByKey: string } => {
  // If splitByKey is TRUTHY (defined and not null) and reportRunner exists, it is a multi-sheet report.
  return !!template.splitByKey && template.reportRunner !== undefined;
};

/** SingleSheetReportRunner Type guard */

export const hasSingleSheetRunner = (
  template: ReportTemplate
): template is ReportTemplate & { reportRunner: SingleSheetReportRunner } => {
  // If splitByKey is FALSY (undefined or null) and reportRunner exists, it is a single-sheet report.
  return !template.splitByKey && template.reportRunner !== undefined;
};

export const getReportData = async (reportDataSource: ReportDataSource): Promise<unknown[]> => {
  return await reportDataSource();
};

const labelAsHumanReadable = (label: string): string => readableLabelOptions[label] ?? label;

export const generateMultiSheetSubheader = async (
  context: Excel.RequestContext,
  splitByKey: string,
  reportType: string
) => {
  const label = labelAsHumanReadable(splitByKey);
  const existingSheets = await getSheetsByReportType(context, reportType);

  if (existingSheets.length > 1) {
    return `${existingSheets.length} ${label}`;
  }
  return reportType;
};

export const getSheetsToDeleteForReportType = async (
  reportType: string,
  data: any[],
  splitByKey: string
): Promise<ReportSheetInfo[]> => {
  // No rows at all means the load failed or filtered to nothing, not that every sheet is orphaned.
  // Reading it the other way proposes deleting all of them, and Excel has no undo for that.
  if (!data.length) return [];

  return Excel.run(async (context) => {
    const sheetsToDelete: ReportSheetInfo[] = [];
    const dictionary = groupDataByKey(data, splitByKey);
    const existingSheetInfo = await getSheetsByReportType(context, reportType);

    for (const { worksheet, label, name } of existingSheetInfo) {
      if (label && !dictionary[label]) {
        sheetsToDelete.push({ worksheet, label, name });
      }
    }
    return sheetsToDelete;
  });
};

/** MultiSheetReportRunner Type guard */
export const reportHasData = async (reportDataSource: ReportDataSource): Promise<boolean> => {
  const data = await reportDataSource();
  return data.length > 0;
};
